<template>
  <b-modal
    ref="modal"
    :title="title"
    size="lg"
    hide-footer
    centered>
    <div class="p-1" v-if="occurrence">
      <!-- Contexto da ocorrência -->
      <div class="border rounded p-1 mb-2">
        <div class="flex justify-between mb-50">
          <small class="text-muted">{{ $t('lesson.agenda.occurrence') }}</small>
          <small class="font-medium capitalize">{{ occurrenceLabel }}</small>
        </div>
        <div class="flex justify-between">
          <small class="text-muted">{{ $t('lesson.agenda.occupancy') }}</small>
          <small>{{ occurrence.occupied }}/{{ occurrence.capacity === null ? '∞' : occurrence.capacity }}</small>
        </div>
      </div>

      <!-- Ajustar vagas -->
      <template v-if="mode === 'capacity'">
        <b-form-group :label="$t('lesson.agenda.new_capacity')">
          <b-form-input
            type="number"
            min="1"
            max="999"
            v-model.number="capacity"
            :placeholder="`${occurrence.capacity === null ? '' : occurrence.capacity}`"/>
        </b-form-group>
        <small class="text-muted d-block mb-2">{{ $t('lesson.agenda.capacity_hint') }}</small>
        <b-alert show variant="warning" class="p-1 mb-2" v-if="capacity && capacity < occurrence.occupied">
          <small>{{ $t('lesson.agenda.capacity_below_occupancy_warning', { occupied: occurrence.occupied }) }}</small>
        </b-alert>
      </template>

      <!-- Bloquear -->
      <template v-if="mode === 'block'">
        <b-alert show variant="warning" class="p-1 mb-2">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-1 flex-shrink-0"/>
            <small>{{ $t('lesson.agenda.block_hint') }}</small>
          </div>
        </b-alert>
      </template>

      <!-- Motivo -->
      <b-form-group :label="$t('cancellation.club_cancel_reason')" v-if="mode === 'cancel'">
        <b-form-select v-model="reason" :options="reasonOptions" class="required"/>
      </b-form-group>
      <b-form-group :label="$t('lesson.agenda.reason')" v-else>
        <b-form-input v-model="reason" :placeholder="$t('lesson.agenda.reason_placeholder')"/>
      </b-form-group>

      <!-- Cancelamento: impacto + remarcação em massa -->
      <template v-if="mode === 'cancel'">
        <div class="border rounded p-1 mb-2" v-if="impact">
          <div class="font-medium mb-1">{{ $t('cancellation.club_cancel_impact') }}</div>
          <div class="flex justify-between mb-50">
            <small class="text-muted">{{ $t('cancellation.club_cancel_students') }}</small>
            <small>{{ impact.affected_students }}</small>
          </div>
          <div class="flex justify-between mb-50">
            <small class="text-muted">{{ $t('cancellation.club_cancel_refund') }}</small>
            <small>{{ formatValue(impact.full_refund_total) }}</small>
          </div>
          <div class="flex justify-between mb-50">
            <small class="text-muted">{{ $t('cancellation.club_cancel_transfer') }}</small>
            <small>{{ formatValue(impact.settled_transfer_reversal) }}</small>
          </div>
          <div class="flex justify-between">
            <small class="text-muted">{{ $t('cancellation.club_cancel_fee') }}</small>
            <small>{{ formatValue(impact.hubee_fee_returned) }}</small>
          </div>
        </div>

        <div class="flex justify-between items-center border rounded p-1 mb-2">
          <div>
            <div>{{ $t('cancellation.club_cancel_mass_reschedule') }}</div>
            <small class="text-muted">{{ $t('cancellation.club_cancel_mass_hint') }}</small>
          </div>
          <b-form-checkbox v-model="offerReschedule" switch/>
        </div>

        <b-alert show variant="danger" class="p-1 mb-2">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-1 flex-shrink-0"/>
            <small>{{ $t('lesson.agenda.cancel_warning') }}</small>
          </div>
        </b-alert>
      </template>

      <!-- Autorizar aluno acima do limite -->
      <template v-if="mode === 'grant'">
        <b-alert show variant="warning" class="p-1 mb-2">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-1 flex-shrink-0"/>
            <small>{{ $t('schedule_event.seat_grant_hint') }}</small>
          </div>
        </b-alert>

        <b-form-group :label="$t('schedule_event.seat_grant_student')">
          <!-- model="User" e não "Student": a autorização é nominal e aponta
               para public.users, que é também quem ocupa o assento em
               scheduled_events.user_id. (Student é ppl.students, sem user_id.) -->
          <select-suggestion
            ref="grantStudent"
            column="name,email"
            model="User"
            v-model="grantStudent"
            :label="null"
            :appendClearOption="true"
            placeholderText="Digite o nome ou e-mail do aluno"
            :itemTextFormat="studentItemTextFormat"/>
        </b-form-group>

        <b-form-group :label="$t('schedule_event.seat_grant_scope')">
          <b-form-radio-group
            v-model="grantScope"
            :options="grantScopeOptions"
            stacked
            value-field="value"
            text-field="label"/>
        </b-form-group>
      </template>

      <!-- Ações -->
      <div class="flex justify-end gap-4">
        <b-button variant="outline-secondary" class="mr-2" @click="close">
          {{ $t('back') }}
        </b-button>
        <b-button
          v-if="mode === 'grant'"
          variant="success"
          :disabled="!grantStudent || !grantStudent.id"
          @click="saveGrant">
          {{ $t('schedule_event.seat_grant') }}
        </b-button>
        <b-button
          v-if="mode === 'capacity'"
          variant="warning"
          :disabled="!capacity"
          @click="saveCapacity">
          {{ $t('lesson.agenda.adjust_capacity') }}
        </b-button>
        <b-button
          v-if="mode === 'block'"
          variant="dark"
          @click="saveBlock">
          {{ $t('lesson.agenda.block') }}
        </b-button>
        <b-button
          v-if="mode === 'cancel'"
          variant="danger"
          :disabled="!reason"
          @click="executeCancel">
          {{ $t('cancellation.club_cancel_confirm', { count: impact ? impact.affected_students : 0 }) }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BAlert,
  BButton,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormRadioGroup,
  BFormSelect,
  BModal
} from 'bootstrap-vue'
import { currencyFormatter } from '@/util/Util'

import CancellationRequestService from '@/services/api/CancellationRequestService'
import LessonService from '@/services/api/LessonService'
import SelectSuggestion from '@/components/SelectSuggestion.vue'

export default {
  components: {
    BAlert,
    BButton,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BFormSelect,
    BModal,
    SelectSuggestion
  },
  props: {
    lessonId: {
      type: Number,
      default: null
    },
    lessonName: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    service: null,
    cancellationService: null,
    mode: null,
    occurrence: null,
    capacity: null,
    reason: '',
    reasonOptions: [
      'Professor indisponível',
      'Manutenção do espaço',
      'Clima',
      'Baixa ocupação',
      'Outro'
    ],
    offerReschedule: true,
    impact: null,
    // Autorização nominal de vaga
    grantStudent: null,
    grantScope: 'OCC'
  }),
  computed: {
    title() {
      const titles = {
        capacity: this.$t('lesson.agenda.adjust_capacity'),
        block: this.$t('lesson.agenda.block'),
        cancel: this.$t('lesson.agenda.cancel_occurrence'),
        grant: this.$t('schedule_event.seat_grant')
      }
      return `${titles[this.mode] || ''}${this.lessonName ? ` · ${this.lessonName}` : ''}`
    },
    occurrenceLabel() {
      if (!this.occurrence) return ''
      const [year, month, day] = this.occurrence.date.split('-')
      return `${day}/${month}/${year} · ${this.occurrence.startTime} - ${this.occurrence.endTime}`
    },
    grantScopeOptions() {
      const labels = this.$t('schedule_event.seat_grant_scope_labels')
      return [
        { value: 'OCC', label: labels.OCC },
        { value: 'SLT', label: labels.SLT }
      ]
    }
  },
  methods: {
    open(mode, occurrence) {
      this.mode = mode
      this.occurrence = occurrence
      this.capacity = occurrence.capacitySource === 'override' ? occurrence.capacity : null
      this.reason = mode === 'cancel' ? this.reasonOptions[0] : (occurrence.reason || '')
      this.offerReschedule = true
      this.impact = null
      // Autorizar é sempre uma decisão nova; herdar o aluno da vez anterior
      // liberaria vaga para quem o professor não escolheu agora.
      this.grantStudent = null
      this.grantScope = 'OCC'
      this.$refs.modal.show()
      if (mode === 'cancel') {
        this.loadImpact()
      }
    },
    close() {
      this.$refs.modal.hide()
    },
    loadImpact() {
      this.cancellationService.clubCancelImpact({
        lesson_id: this.lessonId,
        date: this.occurrence.date,
        start_time: this.occurrence.startTime,
        end_time: this.occurrence.endTime
      }).then(
        impact => { this.impact = impact },
        () => {}
      )
    },
    saveCapacity() {
      this.saveOverride({ capacity: this.capacity, status: 'AC' })
    },
    /**
     * Autorização nominal: abre UMA vaga acima do limite, e só para o aluno
     * escolhido. Diferente de "ajustar vagas", que abre para quem comprar
     * primeiro.
     */
    saveGrant() {
      if (!this.grantStudent || !this.grantStudent.id) return

      this.$vs.loading()
      this.service.createSeatGrant(this.lessonId, {
        user_id: this.grantStudent.id,
        scope: this.grantScope,
        date: this.occurrence.date,
        start_time: this.occurrence.startTime,
        end_time: this.occurrence.endTime,
        reason: this.reason || null
      }).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.close()
          this.$emit('saved')
        },
        error => this.notifyErrors(this.$vs, error)
      ).finally(() => { this.$vs.loading.close() })
    },
    studentItemTextFormat(model) {
      return model && model.email ? `${model.name} (${model.email})` : (model ? model.name : '')
    },
    saveBlock() {
      this.saveOverride({ status: 'BL' })
    },
    saveOverride(extra) {
      this.$vs.loading()
      this.service.saveOccurrenceOverride(this.lessonId, {
        date: this.occurrence.date,
        start_time: this.occurrence.startTime,
        end_time: this.occurrence.endTime,
        reason: this.reason || null,
        ...extra
      }).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.close()
          this.$emit('saved')
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    },
    executeCancel() {
      this.$vs.loading()
      this.service.cancelOccurrence(this.lessonId, {
        date: this.occurrence.date,
        start_time: this.occurrence.startTime,
        end_time: this.occurrence.endTime,
        reason: this.reason,
        offer_reschedule: this.offerReschedule
      }).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.close()
          this.$emit('saved')
        },
        () => {}
      ).finally(() => { this.$vs.loading.close() })
    },
    formatValue(value) {
      return currencyFormatter(value || 0)
    }
  },
  beforeMount() {
    this.service = LessonService.build(this.$vs)
    this.cancellationService = CancellationRequestService.build(this.$vs)
  }
}
</script>
